import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Paper from "@mui/material/Paper";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ColorLensIcon from "@mui/icons-material/ColorLens";

import {
  ColorTab,
  TextTab,
  IconTab,
  PointTab,
} from "./SpreadsheetCardSettingsTabs";
import { CollapseHeading } from "./SpreadsheetSettingSharedComponents";
import { actionSheetFileUpdated, selectorSheet } from "./spreadsheetRedux";

const sectionTitle_ = (theme) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "10px",
  fontWeight: 500,
  "&:hover": {
    cursor: "pointer",
  },
  "& :hover": {
    cursor: "pointer",
  },
  "& button": {
    padding: "0px",
    marginLeft: "4px",
  },
});

const tab_ = (theme) => ({
  minWidth: "20%",
  flexGrow: 1,
});

const indicator_ = (theme) => ({
  height: 5,
  backgroundColor: "var(--fill-color)",
});

const tabPanel_ = (theme) => ({
  paddingTop: "0px",
  paddingLeft: "10px",
  paddingRight: "10px",
  paddingBottom: "10px",
});

const TabPanel = ({ children, value, section, ...other }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== section}
      id={`wrapped-tabpanel-${section}`}
      aria-labelledby={`wrapped-tab-${section}`}
      {...other}
    >
      {value === section && <Box pt={2}>{children}</Box>}
    </Box>
  );
};

export default function SpreadsheetCardSettings({ sheetId }) {
  const { settingsExpanded, settingsTab, type } = useSelector(
    selectorSheet(sheetId),
  );
  const dispatch = useDispatch();

  const handleTabChange = (_event, newValue) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        settingsTab: newValue,
      }),
    );
  };

  const handleSettingsExpand = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        settingsExpanded: !settingsExpanded,
      }),
    );
  };

  useEffect(() => {
    if (type === "point") {
      dispatch(
        actionSheetFileUpdated({
          id: sheetId,
          settingsTab: "point",
        }),
      );
    }
  }, [type]);

  return (
    <>
      <CollapseHeading
        headingClass={sectionTitle_}
        expanded={settingsExpanded}
        setExpanded={handleSettingsExpand}
        headingText="Settings"
      />
      <Collapse in={settingsExpanded}>
        <Paper>
          <Tabs
            slotProps={{
              indicator: {
                sx: indicator_,
              },
            }}
            value={settingsTab}
            onChange={handleTabChange}
          >
            <Tab
              sx={tab_}
              icon={<TextFieldsIcon />}
              value="text"
              label="Text"
              disabled={type === "point"}
            />
            <Tab
              sx={tab_}
              icon={<ColorLensIcon />}
              value="color"
              label="Color"
              disabled={type === "point"}
            />
            <Tab
              sx={tab_}
              icon={<LocationOnIcon />}
              value="icon"
              label="Room Icon"
              disabled={type === "point"}
            />
            <Tab
              sx={tab_}
              icon={<LocationOnIcon />}
              value="point"
              label="Point"
              disabled={type === "room"}
            />
          </Tabs>
          <TabPanel sx={tabPanel_} value={settingsTab} section="text">
            <TextTab sheetId={sheetId} />
          </TabPanel>
          <TabPanel sx={tabPanel_} value={settingsTab} section="color">
            <ColorTab sheetId={sheetId} />
          </TabPanel>
          <TabPanel sx={tabPanel_} value={settingsTab} section="icon">
            <IconTab sheetId={sheetId} />
          </TabPanel>
          <TabPanel sx={tabPanel_} value={settingsTab} section="point">
            <PointTab sheetId={sheetId} />
          </TabPanel>
        </Paper>
      </Collapse>
    </>
  );
}
